import type { DocNode as ADF } from '@atlaskit/adf-schema';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { User } from '@atlassian/jira-servicedesk-insight-object-types/src/common/types/object-details.tsx';
import type { CommentRoleType } from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/shared-types/index.tsx';
import type { Action } from '@atlassian/react-sweet-state';
import type {
	ObjectActivityTimelineState,
	ContainerProps,
} from '../../../../common/types/store.tsx';
import {
	isAddObjectCommentServiceResponseData,
	type TimelineEvent,
	type ObjectCommentTimelineEvent,
	OBJECT_COMMENT_EVENT,
} from '../../../../common/types/timeline-event.tsx';
import { addObjectComment as addObjectCommentService } from '../../../../services/object-activity/index.tsx';

export const addObjectComment =
	({
		role,
		comment,
		actor,
		onFailure,
	}: {
		role: CommentRoleType;
		comment: ADF;
		actor: User;
		onFailure: (error: string) => void;
	}): Action<ObjectActivityTimelineState, ContainerProps> =>
	async ({ setState, getState }, { workspaceId, objectId, createAnalyticsEvent }) => {
		const timestamp = Date.now();

		const optimisticServiceResponse: {
			data: ObjectCommentTimelineEvent;
		} = {
			data: {
				data: {
					id: `optimistic-id-${timestamp}`,
					actor,
					comment,
					role,
					canEdit: false,
					canDelete: false,
					isSaving: true,
					isEdited: false,
					editedTimestamp: timestamp,
				},
				timestamp,
				type: OBJECT_COMMENT_EVENT,
			},
		};

		setState({
			timelineEvents: [optimisticServiceResponse.data, ...(getState().timelineEvents ?? [])],
		});

		const serviceResponse = await addObjectCommentService({
			workspaceId,
			objectId,
			role,
			comment,
		});

		const latestTimelineEvents: TimelineEvent[] = getState().timelineEvents ?? [];
		const latestTimelineEventsWithoutOptimistic: TimelineEvent[] = latestTimelineEvents.filter(
			(timeLineEvent) => timeLineEvent.data.id !== optimisticServiceResponse.data.data.id,
		);

		if (isAddObjectCommentServiceResponseData(serviceResponse)) {
			setState({
				timelineEvents: [serviceResponse.data, ...latestTimelineEventsWithoutOptimistic],
			});

			fireTrackAnalytics(createAnalyticsEvent({}), 'storeObjectComment succeeded');
		} else if (serviceResponse.error) {
			// Revert the optimistic resposone
			setState({
				timelineEvents: latestTimelineEventsWithoutOptimistic,
			});

			onFailure(serviceResponse.error);

			fireErrorAnalytics({
				error: serviceResponse.rawError,
				meta: {
					id: 'storeObjectComment',
					packageName: 'jiraServicedeskInsightObjectActivityTimelineStore',
					teamName: 'falcons',
				},
				attributes: {
					errorType: serviceResponse.error,
					errorMessage: serviceResponse.rawError.message,
				},
			});
		}
	};
